import { Box, Button, Container, Flex, Heading, Image, Text } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import { NavLink, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.js';
import { appConfig, appVersion, brand } from '../lib/env.js';

function NavItem({ to, label }: { to: string; label: string }) {
  return (
    <NavLink to={to} data-name={`nav-${label.toLowerCase()}`}>
      {({ isActive }) => (
        <Text
          px={3}
          py={1}
          borderRadius="md"
          fontWeight={isActive ? 'bold' : 'normal'}
          bg={isActive ? 'whiteAlpha.300' : 'transparent'}
          _hover={{ bg: 'whiteAlpha.200' }}
        >
          {label}
        </Text>
      )}
    </NavLink>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  const { user, isAuthenticated, logout } = useAuth();

  return (
    <Flex direction="column" minH="100vh" bg="brand.pageBg">
      <Box as="header" bg="brand.headerBg" color="white" data-name="admin-header">
        <Container maxW="6xl" py={3}>
          <Flex align="center" gap={4}>
            <RouterLink to="/">
              <Image src={brand.logos.header} alt={brand.name} h="36px" />
            </RouterLink>
            <Heading size="sm" fontFamily="heading">
              {appConfig.appName} — Admin
            </Heading>
            {isAuthenticated && (
              <Flex gap={1} ml={6}>
                <NavItem to="/orders" label="Orders" />
                <NavItem to="/products" label="Products" />
              </Flex>
            )}
            {isAuthenticated && user && (
              <Flex align="center" gap={3} ml="auto">
                <Text fontSize="sm" opacity={0.9} data-name="user-name">
                  {user.name} ({user.roles.join(', ') || 'no role'})
                </Text>
                <Button
                  size="xs"
                  variant="outline"
                  color="white"
                  onClick={logout}
                  data-name="logout"
                >
                  Sign out
                </Button>
              </Flex>
            )}
          </Flex>
        </Container>
      </Box>

      <Box as="main" flex="1" py={8} data-name="admin-main">
        <Container maxW="6xl">{children}</Container>
      </Box>

      <Box as="footer" bg="brand.footerBg" color="white" data-name="admin-footer">
        <Container maxW="6xl" py={3}>
          <Flex fontSize="sm" gap={4}>
            <Text>{brand.copy.footerLine}</Text>
            <Text ml="auto" opacity={0.6}>
              {appVersion}
            </Text>
          </Flex>
        </Container>
      </Box>
    </Flex>
  );
}
